import {
    Category,
    CategoryParameter,
    CategoryNodeSummary,
    CategoryNodeType,
    CategoryBranchCreationRequest,
    PatchCategoryMoveDTO,
    MetadataCategorySearchDTO,
    MetadataParameterSelectableDTO,
    ParameterDataType,
    MetadataCategoryCreationRequestDTO,
    PatchCategoryUpdateNameDTO,
    PatchCategoryUpdateDescriptionDTO,
    PatchCategoryAddParameterDTO,
    PatchCategoryRemoveParameterDTO,
    PatchCategoryAddSelectableParameterDTO,
    PatchCategoryParameterUpdateNameDTO,
    PatchCategoryParameterUpdateDescriptionDTO,
    PatchCategoryChangeSelectableChoiceDTO,
    PatchCategoryRemoveSelectableChoiceDTO,
    PatchCategoryAddChoiceToSelectableParameterDTO,
    PatchCategoryChangeParameterUnitDTO,
    PatchCategoryAddSimilarNameDTO,
    PatchCategoryRemoveSimilarNameDTO,
} from "../types/Category";

import axiosInstance from "./axiosInstance";

function authHeader(token?: string) {
    if (!token || token.trim().length === 0) return {};
    return { Authorization: `Bearer ${token}` };
}

function normalizeCategory(cat: Category): Category {
    return {
        ...cat,
        parameters: (cat.parameters ?? []).map((p) => {
            if (p.dataType === "SELECTABLE") {
                const raw: any[] = (p as any).selectables ?? (p as any).options ?? [];
                return {
                    ...p,
                    options: raw.map((s) => (typeof s === "string" ? s : s.name)),
                };
            }
            return p;
        }),
    };
}



function parameterToCreationDto(
    p: CategoryParameter
): NonNullable<MetadataCategoryCreationRequestDTO["parameters"]>[number] {
    if (p.dataType === ParameterDataType.SELECTABLE) {
        const selectableParam = p as MetadataParameterSelectableDTO;

        const selectables: string[] =
            selectableParam.selectables?.map(s =>
                typeof s === "string" ? s : s.name
            ) ?? [];

        return {
            name: p.name,
            description: p.description,
            dataType: ParameterDataType.SELECTABLE,
            selectionType: selectableParam.selectionType,
            selectables,
        };
    }

    const dto: ReturnType<typeof parameterToCreationDto> = {
        name: p.name,
        description: p.description,
        dataType: p.dataType,
    };
    if ((p.dataType === ParameterDataType.INTEGER || p.dataType === ParameterDataType.DOUBLE) && p.unitId) {
        (dto as any).unitId = p.unitId;
    }
    return dto;
}

function toMetadataCategoryCreationRequest(category: Category): MetadataCategoryCreationRequestDTO {
    return {
        name: category.name,
        description: category.description || undefined,
        parentId: category.parentId ?? undefined,
        globallyUnique: category.globallyUnique || undefined,
        hasServiceArea: category.hasServiceArea || undefined,
        parameters: category.parameters.map(parameterToCreationDto),
        similarNames: category.similarNames ?? [],
    };
}

/** Toggle whether a category is a service category (its listings have a coverage area). */
export async function patchCategoryChangeServiceArea(
    token: string,
    id: string,
    version: number,
    hasServiceArea: boolean
): Promise<CategoryNodeSummary> {
    const res = await axiosInstance.patch<CategoryNodeSummary>(
        "/categories",
        { id, version, patchTask: "ChangeServiceArea", hasServiceArea },
        { headers: authHeader(token) }
    );
    return res.data;
}

/** Sets the display order of the category's parameters (all ids, new order).
 *  The order drives the search form, listing create form, and {features}. */
export async function patchCategoryReorderParameters(
    token: string,
    id: string,
    version: number,
    parameterIds: number[]
): Promise<Category> {
    const res = await axiosInstance.patch<Category>(
        "/categories",
        { id, version, patchTask: "ReorderParameters", parameterIds },
        { headers: authHeader(token) }
    );
    return normalizeCategory(res.data);
}

export async function patchCategoryChangeItemLocation(
    token: string,
    id: string,
    version: number,
    hasItemLocation: boolean
): Promise<CategoryNodeSummary> {
    const res = await axiosInstance.patch<CategoryNodeSummary>(
        "/categories",
        { id, version, patchTask: "ChangeItemLocation", hasItemLocation },
        { headers: authHeader(token) }
    );
    return res.data;
}

export async function autocompleteCategories(
    token: string,
    q: string,
    limit = 8
): Promise<MetadataCategorySearchDTO[]> {
    const resp = await axiosInstance.get<MetadataCategorySearchDTO[]>("/categories/autocompletesearch", {
        params: { q, limit },
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

export async function searchCategories(
    token: string,
    q: string,
    limit = 8
): Promise<MetadataCategorySearchDTO[]> {
    const resp = await axiosInstance.get<MetadataCategorySearchDTO[]>("/categories/search", {
        params: { q, limit },
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

export async function getCategoryById(token: string, id: string): Promise<Category> {
    const resp = await axiosInstance.get<Category>(`/categories/id/${id}`, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

// ---------------------------------------------------------------------------
// Tree navigation (roots / branches / leaves)
// ---------------------------------------------------------------------------

/** Case-insensitive A→Z, applied client-side so every tree view is ordered. */
function byName(list: CategoryNodeSummary[]): CategoryNodeSummary[] {
    return [...list].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
}

/** Root nodes (those without a parent). */
export async function getRoots(token: string): Promise<CategoryNodeSummary[]> {
    const resp = await axiosInstance.get<CategoryNodeSummary[]>("/categories/roots", {
        headers: authHeader(token),
    });
    return byName(resp.data ?? []);
}

/** Direct children of a node. */
export async function getChildren(token: string, id: string): Promise<CategoryNodeSummary[]> {
    const resp = await axiosInstance.get<CategoryNodeSummary[]>(`/categories/${id}/children`, {
        headers: authHeader(token),
    });
    return byName(resp.data ?? []);
}

/** Whole subtree beneath a node (all descendants). */
export async function getSubtree(token: string, id: string): Promise<CategoryNodeSummary[]> {
    const resp = await axiosInstance.get<CategoryNodeSummary[]>(`/categories/${id}/subtree`, {
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

/**
 * Fetches any node by id. A leaf comes back as a full {@link Category} (with
 * parameters); a branch as a {@link CategoryNodeSummary}. Tell them apart via
 * {@code nodeType}.
 */
export async function getCategoryNodeById(
    token: string,
    id: string
): Promise<CategoryNodeSummary | Category> {
    const resp = await axiosInstance.get<CategoryNodeSummary | Category>(`/categories/id/${id}`, {
        headers: authHeader(token),
    });
    const node = resp.data;
    return node.nodeType === CategoryNodeType.LEAF
        ? normalizeCategory(node as Category)
        : (node as CategoryNodeSummary);
}

/**
 * Resolves the breadcrumb (root -> immediate parent) for a node from its
 * ancestor ids. Trees are shallow, so the lookups run in parallel.
 */
export async function getAncestors(
    token: string,
    ancestorIds: string[]
): Promise<CategoryNodeSummary[]> {
    if (!ancestorIds || ancestorIds.length === 0) return [];
    const nodes = await Promise.all(
        ancestorIds.map((id) => getCategoryNodeById(token, id))
    );
    return nodes as CategoryNodeSummary[];
}

/** Creates a grouping branch (a non-leaf node) under an optional parent. */
export async function createBranch(
    token: string,
    dto: CategoryBranchCreationRequest
): Promise<CategoryNodeSummary> {
    const resp = await axiosInstance.post<CategoryNodeSummary>("/categories/branches", dto, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Re-parents a node (and its subtree). Pass null to move it to a root position. */
export async function moveCategory(
    token: string,
    dto: PatchCategoryMoveDTO
): Promise<CategoryNodeSummary> {
    const resp = await axiosInstance.patch<CategoryNodeSummary>(`/categories`, dto, {
        headers: authHeader(token),
    });
    return resp.data;
}

/** Deletes a node; pass cascade=true to remove its whole subtree. */
export async function deleteCategoryNode(
    token: string,
    id: string,
    cascade = false
): Promise<void> {
    await axiosInstance.delete(`/categories/${id}`, {
        params: { cascade },
        headers: authHeader(token),
    });
}

export async function fetchCategories(token: string): Promise<Category[]> {
    const resp = await axiosInstance.get<Category[]>("/categories", {
        headers: authHeader(token),
    });
    return resp.data ?? [];
}

export async function createCategory(token: string, category: Category): Promise<Category> {
    const body = toMetadataCategoryCreationRequest(category);
    const resp = await axiosInstance.post<Category>("/categories", body, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function patchCategoryUpdateName(
    token: string,
    patch: PatchCategoryUpdateNameDTO
): Promise<Category> {
    const resp = await axiosInstance.patch<Category>(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryUpdateDescription(
    token: string,
    patch: PatchCategoryUpdateDescriptionDTO
): Promise<Category> {
    const resp = await axiosInstance.patch<Category>(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryAddSimilarName(
    token: string,
    patch: PatchCategoryAddSimilarNameDTO
): Promise<Category> {
    const resp = await axiosInstance.patch<Category>(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryRemoveSimilarName(
    token: string,
    patch: PatchCategoryRemoveSimilarNameDTO
): Promise<Category> {
    const resp = await axiosInstance.patch<Category>(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryAddParameter(
    token: string,
    patch: PatchCategoryAddParameterDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryRemoveParameter(
    token: string,
    patch: PatchCategoryRemoveParameterDTO
): Promise<void> {
    await axiosInstance.patch(`/categories`, patch, {
        headers: {
            ...authHeader(token),
        },
    });
}


export async function patchCategoryParameterUpdateName(
    token: string,
    patch: PatchCategoryParameterUpdateNameDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryParameterUpdateDescription(
    token: string,
    patch: PatchCategoryParameterUpdateDescriptionDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function PatchCategoryAddSelectableParameter(
    token: string,
    patch: PatchCategoryAddSelectableParameterDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryChangeSelectableChoice(
    token: string,
    patch: PatchCategoryChangeSelectableChoiceDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryRemoveSelectableChoice(
    token: string,
    patch: PatchCategoryRemoveSelectableChoiceDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}


export async function PatchCategoryAddChoiceToSelectableParameter(
    token: string,
    patch: PatchCategoryAddChoiceToSelectableParameterDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function patchCategoryChangeParameterUnit(
    token: string,
    patch: PatchCategoryChangeParameterUnitDTO
): Promise<Category> {
    const resp = await axiosInstance.patch(`/categories`, patch, {
        headers: authHeader(token),
    });
    return normalizeCategory(resp.data);
}

export async function exportCategory(token: string, id: string): Promise<unknown> {
    const resp = await axiosInstance.get(`/categories/${id}/export`, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function importCategory(token: string, dto: unknown): Promise<Category> {
    const resp = await axiosInstance.post<Category>(`/categories/import`, dto, {
        headers: authHeader(token),
    });
    return resp.data;
}

export async function transferCategories(
    token: string,
    categoryIds: string[],
    targetUrl: string,
    targetToken: string
): Promise<{ succeeded: string[]; failed: { categoryName: string; reason: string }[] }> {
    const resp = await axiosInstance.post(`/categories/transfer`, { categoryIds, targetUrl, targetToken }, {
        headers: authHeader(token),
    });
    return resp.data;
}

